import React from 'react';

// Scorecard body: pre-check (can Evaluate run LIVE?) + every number labelled with where
// it came from — LIVE (golden test suite on the real model), PUBLIC BENCHMARK (BenchLM /
// Artificial Analysis), PUBLISHED PRICE, BLENDED, or SIMULATED (never measured).
// Data comes from item.scorecard_view, built by backend/scorecard_view.py.
// Written with React.createElement (no JSX) so it can be render-tested without a build.
const h = React.createElement;

const PROV = {
  LIVE: { t: 'LIVE', bg: '#d4edda', fg: '#155724', bd: '#a3cfbb', title: 'Measured by Evaluate: golden test suite run against the real model' },
  PUBLIC_BENCHMARK: { t: 'PUBLIC BENCHMARK', bg: '#e7f0ff', fg: '#084298', bd: '#b6d4fe', title: 'Published third-party benchmark result' },
  PUBLISHED_PRICE: { t: 'PUBLISHED PRICE', bg: '#e6f6f4', fg: '#0f5c55', bd: '#9fd8d0', title: 'Provider list price' },
  BLENDED: { t: 'BLENDED', bg: '#f1e8ff', fg: '#5a2d91', bd: '#d4bff5', title: 'Computed from the live score and a public benchmark' },
  SIMULATED: { t: 'SIMULATED', bg: '#f1f1f1', fg: '#555', bd: '#bbb', title: 'Estimate or placeholder: never measured, not used for ranking or approval' },
};
const STATUS = {
  PASS: { icon: '✅', bg: '#d4edda', fg: '#155724', bd: '#a3cfbb' },
  FAIL: { icon: '❌', bg: '#f8d7da', fg: '#721c24', bd: '#f1aeb5' },
  SELF_HOST: { icon: '🖥️', bg: '#e6f6f4', fg: '#0f5c55', bd: '#9fd8d0' },
  REVIEW: { icon: '🔎', bg: '#fff3cd', fg: '#856404', bd: '#ffe69c' },
  UNKNOWN: { icon: '❔', bg: '#fff3cd', fg: '#856404', bd: '#ffe69c' },
  'N/A': { icon: '–', bg: '#f5f5f5', fg: '#777', bd: '#ddd' },
};
const PLATFORM = { bedrock: 'Amazon Bedrock', google_vertex: 'Google Vertex', self_hosted: 'Self-hosted / temporary GPU',
                   self_hosted_ephemeral: 'temporary GPU in our AWS' };
const NO_DATA = "This model can't be evaluated (it is not available on Amazon Bedrock or Google Vertex) and no public benchmark (BenchLM or Artificial Analysis) is available.";

function Badge({ prov }) {
  const p = PROV[prov] || PROV.SIMULATED;
  return h('span', { title: p.title, style: { fontSize: '9px', fontWeight: 'bold', letterSpacing: '0.3px', padding: '1px 6px', borderRadius: '8px',
    background: p.bg, color: p.fg, border: `1px ${prov === 'SIMULATED' ? 'dashed' : 'solid'} ${p.bd}`, whiteSpace: 'nowrap' } }, p.t);
}

function Tile({ m }) {
  const sim = m.provenance === 'SIMULATED';
  const unit = m.unit === '%' ? '%' : m.unit ? ` ${m.unit}` : '';
  return h('div', { style: { background: sim ? '#fafafa' : '#fff', border: `1px ${sim ? 'dashed' : 'solid'} #e0e0e0`, borderRadius: '4px', padding: '7px 8px' } },
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' } },
      h('span', { style: { fontSize: '11px', color: '#333' } }, m.label),
      h(Badge, { prov: m.provenance })),
    h('strong', { style: { fontSize: '16px', color: sim ? '#888' : '#111', fontStyle: sim ? 'italic' : 'normal' } }, `${m.value}${unit}`),
    h('div', { style: { fontSize: '10px', color: '#888', marginTop: '2px' } }, [m.source, m.note].filter(Boolean).join(' · ')));
}

function Section({ title, children, hint }) {
  return h('div', { style: { marginBottom: '10px' } },
    h('div', { style: { fontSize: '11px', fontWeight: 'bold', color: '#0073bb', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' } },
      title, hint ? h('span', { style: { fontWeight: 'normal', textTransform: 'none', color: '#888', marginLeft: '6px' } }, hint) : null),
    children);
}

const grid = (metrics) => h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' } },
  metrics.map(m => h(Tile, { key: m.key, m })));
const muted = (text) => h('div', { style: { fontSize: '11px', color: '#888' } }, text);

function Precheck({ pc }) {
  const s = STATUS[pc.status] || STATUS.UNKNOWN;
  const title = { PASS: 'Pre-check PASS — Evaluate will run LIVE with the golden test suite',
                  FAIL: 'Pre-check FAIL — no live evaluation possible',
                  SELF_HOST: 'Pre-check: self-hostable — Evaluate on a temporary GPU runs LIVE inside our AWS account',
                  UNKNOWN: 'Pre-check not confirmed — Evaluate will try a live run' }[pc.status] || 'Pre-check';
  return h('div', { style: { background: s.bg, border: `1px solid ${s.bd}`, color: s.fg, borderRadius: '4px', padding: '7px 9px', marginBottom: '10px', fontSize: '11px' } },
    h('strong', { style: { fontSize: '12px' } }, `${s.icon} ${title}`),
    h('div', { style: { marginTop: '2px' } }, pc.message),
    h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '5px' } },
      (pc.checks || []).map(c => {
        const cs = STATUS[c.status] || STATUS.UNKNOWN;
        return h('span', { key: c.platform, title: c.detail || '', style: { background: '#fff', border: `1px solid ${cs.bd}`, color: cs.fg, borderRadius: '10px', padding: '1px 7px', fontSize: '10px' } },
          `${cs.icon} ${PLATFORM[c.platform] || c.platform}: ${c.detail || c.status}`);
      })));
}

const AVAIL = {
  AVAILABLE: { icon: '✅', t: 'Available', fg: '#155724', bg: '#d4edda', bd: '#a3cfbb' },
  BLOCKED: { icon: '⛔', t: 'Blocked', fg: '#721c24', bg: '#f8d7da', bd: '#f1aeb5' },
  OFFERED: { icon: '❔', t: 'Offered (not checked)', fg: '#856404', bg: '#fff3cd', bd: '#ffe69c' },
  NOT_AVAILABLE: { icon: '–', t: 'Not available', fg: '#666', bg: '#f5f5f5', bd: '#ddd' },
  PENDING: { icon: '⏳', t: 'Validation pending', fg: '#5a4b00', bg: '#fffbe6', bd: '#f1e3a0' },
};

function Platforms({ rows }) {
  return h(Section, { title: 'Platform availability' },
    h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' } },
      (rows || []).map(r => {
        const a = AVAIL[r.status] || AVAIL.NOT_AVAILABLE;
        return h('div', { key: r.platform, title: r.detail, style: { background: a.bg, border: `1px solid ${a.bd}`, borderRadius: '4px', padding: '6px 8px' } },
          h('div', { style: { fontSize: '11px', fontWeight: 'bold', color: '#222' } }, r.label),
          h('div', { style: { fontSize: '12px', fontWeight: 'bold', color: a.fg } }, `${a.icon} ${a.t}`),
          h('div', { style: { fontSize: '10px', color: '#666', marginTop: '2px' } }, r.detail),
          r.pending && r.status !== 'PENDING' ? h('div', { style: { fontSize: '10px', color: '#8a6d00', marginTop: '2px' } }, r.pending) : null);
      })));
}

const money = (v) => (v === null || v === undefined) ? '—' : `$${Number(v).toFixed(2)}`;
const pct = (v) => (v === null || v === undefined) ? '—' : `${(Number(v) * 100).toFixed(1)}%`;

// Security pre-check: latest promptfoo red-team result + a button to run one on a model
// that is already callable (Bedrock / deployed endpoint). Open-weight models that aren't
// deployed get the red team inside "Evaluate on temporary GPU".
function Security({ sec, item, pc, getAuthHeaders, canRun, onUpdated }) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [progress, setProgress] = React.useState('');
  const run = async () => {
    if (typeof window !== 'undefined' && !window.confirm(`Run the promptfoo security red team against ${item.model_id}?\n\n` +
      'Attacks are generated and graded on Amazon Bedrock; nothing is sent to promptfoo\'s servers. Takes roughly 15–45 min.')) return;
    setBusy(true); setErr(''); setProgress('Starting…');
    try {
      const r = await fetch('/api/redteam-run', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ model_id: item.model_id }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      let since = 0;
      for (let i = 0; i < 900; i++) {
        await new Promise(res => setTimeout(res, 5000));
        const jr = await fetch(`/api/evaluate-live/${d.job_id}?since=${since}`, { headers: getAuthHeaders() });
        const job = await jr.json().catch(() => ({}));
        since = job.next || since;
        const last = (job.events || []).slice(-1)[0];
        if (last) setProgress(last.text);
        if (job.status === 'DONE' || job.status === 'FAILED') {
          if (job.status === 'FAILED') setErr(job.error || 'red team failed');
          if (onUpdated) onUpdated(null);
          break;
        }
      }
    } catch (e) { setErr(e.message); setProgress(''); }
    setBusy(false);
  };
  const callable = pc.status === 'PASS' && ['bedrock', 'self_hosted'].includes(pc.live_platform);
  const v = sec && (STATUS[sec.verdict] || STATUS.UNKNOWN);
  const sevs = ['critical', 'high', 'medium', 'low'];
  const cell = { padding: '3px 5px', borderBottom: '1px solid #eee' };
  return h(Section, { title: 'Security pre-check — red team (promptfoo)', hint: sec && sec.ran_at ? `run ${sec.ran_at}${sec.platform ? ` on ${PLATFORM[sec.platform] || sec.platform}` : ''}` : null },
    !sec ? muted(pc.status === 'SELF_HOST' || pc.status === 'UNKNOWN'
      ? 'Not run yet. For an open-weight model it runs as part of Evaluate on a temporary GPU (when enabled in Admin).'
      : 'Not run yet.')
      : h('div', { style: { fontSize: '11px' } },
        h('div', { style: { background: v.bg, border: `1px solid ${v.bd}`, color: v.fg, borderRadius: '4px', padding: '6px 8px', marginBottom: '6px' } },
          h('strong', null, `${v.icon} ${sec.verdict}`), ` — ${sec.why || ''}`,
          sec.asr !== undefined && sec.asr !== null ? h('div', null, `Overall attack-success rate ${pct(sec.asr)} (${sec.attacks_succeeded} of ${sec.graded} graded attacks; ${sec.errors || 0} errored)`) : null),
        sec.by_severity ? h('table', { style: { width: '100%', borderCollapse: 'collapse', marginBottom: '6px' } },
          h('thead', null, h('tr', { style: { background: '#f5f7fa', textAlign: 'left' } },
            ['Severity', 'Tests', 'Attacks succeeded', 'Attack-success rate'].map(x => h('th', { key: x, style: cell }, x)))),
          h('tbody', null, sevs.filter(s => sec.by_severity[s]).map(s => h('tr', { key: s },
            h('td', { style: { ...cell, fontWeight: 'bold', textTransform: 'capitalize' } }, s),
            h('td', { style: cell }, sec.by_severity[s].tests),
            h('td', { style: cell }, sec.by_severity[s].attacks_succeeded),
            h('td', { style: cell }, pct(sec.by_severity[s].asr)))))) : null,
        (sec.by_plugin || []).filter(p => p.attacks_succeeded).length ? h('details', null,
          h('summary', { style: { cursor: 'pointer', color: '#0073bb' } }, 'Where attacks succeeded'),
          h('table', { style: { width: '100%', borderCollapse: 'collapse', margin: '4px 0' } },
            h('tbody', null, sec.by_plugin.filter(p => p.attacks_succeeded).map(p => h('tr', { key: p.plugin },
              h('td', { style: { ...cell, fontFamily: 'monospace' } }, p.plugin),
              h('td', { style: cell }, p.severity),
              h('td', { style: cell }, `${p.attacks_succeeded}/${p.tests} (${pct(p.asr)})`))))),
          (sec.examples || []).slice(0, 5).map((e, i) => h('div', { key: i, style: { border: '1px solid #f1aeb5', background: '#fff8f8', borderRadius: '4px', padding: '5px 7px', margin: '4px 0' } },
            h('div', { style: { fontWeight: 'bold' } }, `${e.plugin} · ${e.severity} · strategy ${e.strategy}`),
            h('div', { style: { color: '#555' } }, `Attack: ${e.prompt}`),
            h('div', { style: { color: '#555' } }, `Model: ${e.response}`),
            e.reason ? h('div', { style: { color: '#888' } }, `Grader: ${e.reason}`) : null))) : null,
        h('div', { style: { color: '#999', fontSize: '10px', marginTop: '4px' } },
          `${(sec.plugins || []).length} plugins × ${sec.num_tests} tests · strategies: ${(sec.strategies || []).join(', ')} · grader: ${sec.provider}` +
          `${sec.runtime_minutes !== undefined ? ` · ${sec.runtime_minutes} min` : ''}`),
        sec.data_policy ? h('div', { style: { color: '#999', fontSize: '10px' } }, sec.data_policy) : null,
        (sec.notes || []).length ? h('details', { style: { fontSize: '10px', color: '#888' } },
          h('summary', { style: { cursor: 'pointer' } }, `${sec.notes.length} note(s)`),
          sec.notes.map((n, i) => h('div', { key: i }, n))) : null),
    canRun && callable ? h('button', { onClick: run, disabled: busy, style: { marginTop: '6px', padding: '4px 10px', fontSize: '11px', border: '1px solid #721c24', background: busy ? '#eee' : '#fff', color: '#721c24', borderRadius: '4px', cursor: busy ? 'not-allowed' : 'pointer' } },
      busy ? '⏳ Red team running…' : (sec ? '↻ Re-run security red team' : '🛡️ Run security red team')) : null,
    progress ? h('div', { style: { fontSize: '11px', color: '#2b6cb0', marginTop: '4px' } }, progress) : null,
    err ? h('div', { style: { color: '#721c24', fontSize: '11px' } }, err) : null);
}

function SelfHosting({ sh, item, getAuthHeaders, canRun, canLaunch, onUpdated, lastRun }) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [gpu, setGpu] = React.useState(null);      // ephemeral settings readiness
  const [progress, setProgress] = React.useState('');
  const chk = sh.check;
  React.useEffect(() => {
    if (!canLaunch || !getAuthHeaders) return;
    fetch('/api/ephemeral-gpu', { headers: getAuthHeaders() }).then(r => r.json()).then(setGpu).catch(() => {});
  }, [canLaunch]);
  const post = async (url, body) => {
    const r = await fetch(url, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
    return d;
  };
  const recheck = async () => {
    setBusy(true); setErr('');
    try { const d = await post('/api/selfhost-check', { model_id: item.model_id }); if (onUpdated && d.item) onUpdated(d.item); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const rec = chk && (chk.recommended || (chk.options || []).find(o => o.recommended));
  const launch = async () => {
    if (!rec) return;
    const msg = `Launch a temporary ${rec.instance_type} (${rec.gpu_count}× ${rec.gpu}, ${rec.precision}) at ${money(rec.usd_per_hour)}/hour, ` +
      `serve ${item.model_id} with vLLM, run the golden test suite, then TERMINATE the instance?\n\n` +
      `Estimated ~${rec.est_eval_minutes || '?'} min ≈ ${money(rec.est_eval_usd)}. The instance self-destructs after ${(gpu && gpu.settings && gpu.settings.max_runtime_minutes) || 90} min at the latest.`;
    if (typeof window !== 'undefined' && !window.confirm(msg)) return;
    setBusy(true); setErr(''); setProgress('Launching…');
    try {
      const d = await post('/api/ephemeral-eval', { model_id: item.model_id });
      let since = 0;
      for (let i = 0; i < 900; i++) {
        await new Promise(res => setTimeout(res, 4000));
        const jr = await fetch(`/api/evaluate-live/${d.job_id}?since=${since}`, { headers: getAuthHeaders() });
        const job = await jr.json().catch(() => ({}));
        since = job.next || since;
        const last = (job.events || []).slice(-1)[0];
        if (last) setProgress(last.text);
        if (job.status === 'DONE' || job.status === 'FAILED') {
          if (job.status === 'FAILED') setErr(job.error || 'evaluation failed');
          setProgress(job.status === 'DONE' ? 'Done — the GPU instance was terminated.' : 'Stopped — the GPU instance was terminated.');
          if (onUpdated) onUpdated((job.result && job.result.scorecard) || null);
          break;
        }
      }
    } catch (e) { setErr(e.message); setProgress(''); }
    setBusy(false);
  };
  const st = (x) => STATUS[x] || STATUS.UNKNOWN;
  const size = chk && chk.size;
  const exactNote = sh.exact_version === false ? '(this version is API-only; sizes shown are for its newest open-weight release, which is a different model)' : null;
  const launchBlock = (() => {
    if (!canLaunch || !chk) return null;
    if (sh.exact_version === false) return muted('Temporary-GPU evaluation is only for models whose own weights are published.');
    if (!rec) return muted('No GPU that fits can be provisioned right now, so it can\'t be evaluated on a temporary GPU.');
    if (gpu && !gpu.ready) return muted(`Temporary-GPU evaluation isn't configured yet (missing: ${(gpu.missing || []).join(', ')}). Set it up in Pre-screen Rules › Ephemeral GPU settings.`);
    return h('button', { onClick: launch, disabled: busy, style: { marginTop: '6px', padding: '5px 10px', fontSize: '11px', fontWeight: 'bold', border: 'none', background: busy ? '#a0aec0' : '#232f3e', color: '#fff', borderRadius: '4px', cursor: busy ? 'wait' : 'pointer' } },
      busy ? '⏳ Running on temporary GPU…' : `🚀 Evaluate on temporary ${rec.instance_type} (~${money(rec.est_eval_usd)}, terminated after)`);
  })();
  return h(Section, { title: 'Self-hosting on AWS GPU', hint: exactNote },
    sh.evidence ? muted(sh.evidence) : null,
    !chk ? muted('GPU fit, cost, provisioning and deployment not checked yet.') : h('div', { style: { fontSize: '11px', color: '#333', marginTop: '4px' } },
      h('div', null, 'Model size: ',
        h('strong', null, size && size.billions ? `${size.billions}B parameters` : 'unknown'),
        size && size.note ? h('span', { style: { color: '#888' } }, ` — ${size.note}`) : null),
      chk.need_gb ? h('div', null, `GPU memory needed (estimate, weights × ${chk.overhead_factor}): BF16 ${chk.need_gb.bf16} GB · FP8 ${chk.need_gb.fp8} GB`) : null,
      rec ? h('div', { style: { margin: '6px 0', padding: '6px 8px', background: '#e6f6f4', border: '1px solid #9fd8d0', borderRadius: '4px' } },
        h('strong', { style: { color: '#0f5c55' } }, `💲 Cheapest GPU that fits and can be provisioned: ${rec.instance_type}`),
        h('div', null, `${rec.gpu_count}× ${rec.gpu} (${rec.gpu_mem_gb} GB) · ${rec.precision} · ${money(rec.usd_per_hour)}/hour On-Demand`),
        h('div', { style: { color: '#555' } }, `One evaluation (launch → vLLM → golden suite → terminate): ~${rec.est_eval_minutes || '?'} min ≈ ${money(rec.est_eval_usd)} (estimate). The instance is destroyed afterwards.`))
        : null,
      (chk.options || []).length ? h('table', { style: { width: '100%', borderCollapse: 'collapse', margin: '5px 0', fontSize: '11px' } },
        h('thead', null, h('tr', { style: { background: '#f5f7fa', textAlign: 'left' } },
          ['Instance', 'GPUs', 'Precision', '$/hour', 'Est. eval', 'Can provision?'].map(x => h('th', { key: x, style: { padding: '4px', borderBottom: '1px solid #ddd' } }, x)))),
        h('tbody', null, chk.options.map(o => h('tr', { key: o.instance_type, style: { background: o.recommended ? '#f0faf8' : 'transparent' } },
          h('td', { style: { padding: '4px', fontWeight: 'bold' } }, `${o.recommended ? '★ ' : ''}${o.instance_type}`),
          h('td', { style: { padding: '4px' } }, `${o.gpu_count}× ${o.gpu} (${o.gpu_mem_gb} GB)`),
          h('td', { style: { padding: '4px' } }, `${o.precision} · needs ${o.need_gb} GB`),
          h('td', { style: { padding: '4px' } }, money(o.usd_per_hour)),
          h('td', { style: { padding: '4px' } }, o.est_eval_usd !== null && o.est_eval_usd !== undefined ? `~${o.est_eval_minutes} min · ${money(o.est_eval_usd)}` : '—'),
          h('td', { style: { padding: '4px', color: st(o.provision).fg }, title: o.provision_detail || '' },
            `${st(o.provision).icon} ${o.provision}`, h('div', { style: { color: '#777', fontSize: '10px' } }, o.provision_detail)))))) : null,
      h('div', { style: { color: st(chk.deployed && chk.deployed.status).fg } },
        `${st(chk.deployed && chk.deployed.status).icon} Already deployed: `, (chk.deployed && chk.deployed.detail) || 'unknown'),
      h('div', { style: { color: '#999', fontSize: '10px', marginTop: '2px' } },
        `Checked ${chk.checked_at} in ${chk.region} · GPU specs: ${chk.specs_source} · prices: ${chk.price_source || 'unavailable'}`)),
    lastRun ? h('div', { style: { fontSize: '11px', marginTop: '6px', color: lastRun.error ? '#721c24' : '#155724' } },
      `Last temporary-GPU run: ${lastRun.instance_type} ${lastRun.instance_id || ''} · ${lastRun.runtime_minutes ?? '?'} min · ≈ ${money(lastRun.est_cost_usd)} · `,
      lastRun.terminated ? 'instance terminated ✅' : `⚠️ termination not confirmed (self-destructs by ${lastRun.expires_at})`,
      lastRun.error ? ` · error: ${lastRun.error}` : '') : null,
    h('div', { style: { display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' } },
      canRun ? h('button', { onClick: recheck, disabled: busy, style: { marginTop: '6px', padding: '4px 10px', fontSize: '11px', border: '1px solid #0073bb', background: busy ? '#eee' : '#fff', color: '#0073bb', borderRadius: '4px', cursor: busy ? 'wait' : 'pointer' } },
        chk ? '↻ Re-check GPU, price & deployment' : '🖥️ Check GPU, price & deployment') : null,
      launchBlock),
    progress ? h('div', { style: { fontSize: '11px', color: '#2b6cb0', marginTop: '4px' } }, progress) : null,
    err ? h('div', { style: { color: '#721c24', fontSize: '11px' } }, err) : null);
}

export default function ScorecardView({ item, getAuthHeaders, canRun, canLaunch, onUpdated }) {
  const v = item && item.scorecard_view;
  if (!v || v.error) return muted(v && v.error ? `Scorecard view error: ${v.error}` : 'Refresh the catalog to load the scorecard pre-check.');
  const pc = v.precheck || {};
  const live = (v.live && v.live.metrics) || [];
  const benchmarks = (v.public || []).filter(m => m.provenance === 'PUBLIC_BENCHMARK');
  const prices = (v.public || []).filter(m => m.provenance !== 'PUBLIC_BENCHMARK');
  const sim = v.simulated || [];
  return h('div', null,
    h(Precheck, { pc }),
    v.platforms ? h(Platforms, { rows: v.platforms }) : null,
    h(Section, { title: 'Live evaluation — golden test suite', hint: v.live && v.live.evaluated_at ? `run ${v.live.evaluated_at} on ${PLATFORM[v.live.platform] || v.live.platform}` : null },
      live.length ? grid(live)
        : muted(pc.status === 'FAIL' ? 'Not possible for this model (see pre-check).'
          : pc.status === 'SELF_HOST' ? 'Not run yet — use Evaluate on a temporary GPU (Self-hosting section below).' : 'Not run yet — click Evaluate.'),
      v.blended ? h('div', { style: { marginTop: '6px' } }, grid([v.blended])) : null),
    h(Section, { title: 'Public benchmarks' },
      benchmarks.length ? grid(benchmarks) : muted('No public benchmark found (BenchLM, Artificial Analysis).'),
      prices.length ? h('div', { style: { marginTop: '6px' } }, grid(prices)) : null),
    sim.length ? h(Section, { title: 'Simulated — not measured', hint: 'shown for reference only; never used for ranking or approval' }, grid(sim)) : null,
    h(Security, { sec: v.security, item, pc, getAuthHeaders, canRun, onUpdated }),
    v.summary === 'NO_DATA' && pc.status === 'FAIL'
      ? h('div', { style: { fontSize: '11px', background: '#fff3cd', border: '1px solid #ffe69c', color: '#856404', borderRadius: '4px', padding: '6px 8px', marginBottom: '10px' } }, `⚠️ ${NO_DATA}`)
      : null,
    v.self_hosted ? h(SelfHosting, { sh: v.self_hosted, item, getAuthHeaders, canRun, canLaunch, onUpdated, lastRun: v.ephemeral_eval }) : null);
}

// Small chip for the catalog table: can Evaluate run live?
export function PrecheckChip({ item }) {
  const pc = item && item.scorecard_view && item.scorecard_view.precheck;
  if (!pc) return null;
  const s = STATUS[pc.status] || STATUS.UNKNOWN;
  const label = { PASS: `Live eval ✓ (${PLATFORM[pc.live_platform] || ''})`, FAIL: 'Live eval ✗', UNKNOWN: 'Live eval ?',
                  SELF_HOST: 'Live eval ✓ (temporary GPU)' }[pc.status];
  return h('span', { title: pc.message, style: { marginLeft: '4px', fontSize: '9px', fontWeight: 'bold', color: s.fg, background: s.bg, border: `1px solid ${s.bd}`, borderRadius: '8px', padding: '1px 6px', whiteSpace: 'nowrap' } }, label);
}
